/**
 * ChartRenderer Component
 * Universal chart rendering component for Reports AI using Recharts
 * Supports: bar, column, line, area, pie, donut, scatter charts
 */

import React, { useRef, useState } from 'react';
import {
  BarChart, Bar,
  LineChart, Line,
  AreaChart, Area,
  PieChart, Pie, Cell,
  ScatterChart, Scatter,
  XAxis, YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import html2canvas from 'html2canvas';
import { message } from 'antd';
import copyIcon from '../../../../assets/copy.svg';
import './ChartRenderer.css';

// Default color palette
const DEFAULT_COLORS = [
  '#0066cc', // Blue
  '#00cc66', // Green
  '#ffcc00', // Yellow
  '#cc0000', // Red
  '#9933cc', // Purple
  '#ff6600', // Orange
  '#00cccc', // Cyan
  '#cc0066'  // Pink
];

/**
 * ChartWithCopyButton Component
 * Wraps charts with a copy button in the top-right corner to export as PNG
 */
const ChartWithCopyButton = ({ children, chartTitle }) => {
  const chartRef = useRef(null);
  const [isCopying, setIsCopying] = useState(false);

  const handleCopy = async () => {
    if (!chartRef.current) return;

    try {
      setIsCopying(true);

      // Use html2canvas to capture the chart as an image
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2, // Higher quality export
        logging: false,
        useCORS: true
      });

      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        if (!blob) {
          throw new Error('Failed to create image blob');
        }

        try {
          // Try to copy to clipboard (requires HTTPS or localhost)
          await navigator.clipboard.write([
            new ClipboardItem({
              'image/png': blob
            })
          ]);
          message.success('Chart copied to clipboard as image');
        } catch (clipboardError) {
          // Fallback: Download the image if clipboard API fails
          console.warn('Clipboard API failed, downloading instead:', clipboardError);
          
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          const timestamp = new Date().getTime();
          const filename = chartTitle 
            ? `chart-${chartTitle.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${timestamp}.png`
            : `chart-${timestamp}.png`;
          
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          message.success('Chart downloaded as PNG');
        }
      }, 'image/png');

    } catch (err) {
      console.error('Failed to export chart:', err);
      message.error('Failed to export chart');
    } finally {
      setIsCopying(false);
    }
  };

  return (
    <div className="chart-with-copy" ref={chartRef}>
      <button
        className="chart-copy-button"
        onClick={handleCopy}
        disabled={isCopying}
        title="Copy chart as image"
      >
        <img src={copyIcon} alt="Copy" width="16" height="16" />
      </button>
      {children}
    </div>
  );
};

const ChartRenderer = ({ chartSpec }) => {
  if (!chartSpec || !chartSpec.type || !chartSpec.data) {
    return (
      <div className="chart-error">
        <p>Invalid chart specification</p>
      </div>
    );
  }

  const { type, title, description, data, config } = chartSpec;
  const colors = config && config.colors ? config.colors : DEFAULT_COLORS;



  /**
   * Render Bar or Column Chart
   */
  const renderBarChart = () => {
    const isVertical = type === 'bar' || (config && config.orientation === 'vertical');

    return (
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={data} layout={isVertical ? 'vertical' : 'horizontal'} margin={{ left: 20, right: 20, top: 20, bottom: 20 }} barSize={10}>
          <CartesianGrid strokeDasharray="3 3" />
          {isVertical ? (
            <>
              <XAxis type="number" label={{ value: config.yAxis.label, position: 'insideBottom', offset: -5 }} />
              <YAxis 
                type="category" 
                dataKey={config.xAxis.dataKey} 
                width={250}
                label={{ value: config.xAxis.label, angle: -90, position: 'insideLeft', offset: 10 }} 
              />
            </>
          ) : (
            <>
              <XAxis dataKey={config.xAxis.dataKey} label={{ value: config.xAxis.label, position: 'insideBottom', offset: -5 }} />
              <YAxis label={{ value: config.yAxis.label, angle: -90, position: 'insideLeft' }} />
            </>
          )}
          <Tooltip />
          <Legend 
            verticalAlign="bottom" 
            align="right"
            wrapperStyle={{ paddingTop: '10px' }}
          />
          <Bar dataKey={config.yAxis.dataKey} fill={colors[0]} radius={[5, 5, 5, 5]} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  /**
   * Render Line Chart (single or multi-series)
   */
  const renderLineChart = () => {
    const seriesKeys = config.seriesKeys || [config.yAxis.dataKey];
    const seriesLabels = config.seriesLabels || {};

    return (
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={data} margin={{ left: 20, right: 20, top: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={config.xAxis.dataKey} label={{ value: config.xAxis.label, position: 'insideBottom', offset: -5 }} />
          <YAxis label={{ value: config.yAxis.label, angle: -90, position: 'insideLeft', offset: 10 }} />
          <Tooltip />
          <Legend 
            verticalAlign="bottom" 
            align="right"
            wrapperStyle={{ paddingTop: '10px' }}
          />
          {seriesKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              name={seriesLabels[key] || key}
              stroke={colors[index % colors.length]}
              strokeWidth={2}
              dot={{ r: 4 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  /**
   * Render Area Chart (single or multi-series)
   */
  const renderAreaChart = () => {
    const seriesKeys = config.seriesKeys || [config.yAxis.dataKey];
    const seriesLabels = config.seriesLabels || {};

    return (
      <ResponsiveContainer width="100%" height={400}>
        <AreaChart data={data} margin={{ left: 20, right: 20, top: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={config.xAxis.dataKey} label={{ value: config.xAxis.label, position: 'insideBottom', offset: -5 }} />
          <YAxis label={{ value: config.yAxis.label, angle: -90, position: 'insideLeft', offset: 10 }} />
          <Tooltip />
          <Legend 
            verticalAlign="bottom" 
            align="right"
            wrapperStyle={{ paddingTop: '10px' }}
          />
          {seriesKeys.map((key, index) => (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              name={seriesLabels[key] || key}
              fill={colors[index % colors.length]}
              stroke={colors[index % colors.length]}
              fillOpacity={0.6}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  /**
   * Render Pie or Donut Chart
   */
  const renderPieChart = () => {
    const isDonut = type === 'donut';
    const innerRadius = isDonut ? '40%' : 0;

    return (
      <ResponsiveContainer width="100%" height={400}>
        <PieChart>
          <Pie
            data={data}
            dataKey={config.valueKey}
            nameKey={config.nameKey}
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius="70%"
            fill="#8884d8"
            label={config.showPercentage !== false}
            labelLine={config.showPercentage !== false}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend 
            verticalAlign="bottom" 
            align="right"
            wrapperStyle={{ paddingTop: '10px' }}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  /**
   * Render Scatter Chart
   */
  const renderScatterChart = () => {
    return (
      <ResponsiveContainer width="100%" height={400}>
        <ScatterChart margin={{ left: 20, right: 20, top: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={config.xAxis.dataKey} type="number" label={{ value: config.xAxis.label, position: 'insideBottom', offset: -5 }} />
          <YAxis dataKey={config.yAxis.dataKey} type="number" label={{ value: config.yAxis.label, angle: -90, position: 'insideLeft', offset: 10 }} />
          <Tooltip cursor={{ strokeDasharray: '3 3' }} />
          <Legend 
            verticalAlign="bottom" 
            align="right"
            wrapperStyle={{ paddingTop: '10px' }}
          />
          <Scatter name={title} data={data} fill={colors[0]} />
        </ScatterChart>
      </ResponsiveContainer>
    );
  };

  /**
   * Select and render appropriate chart type
   */
  const renderChart = () => {
    try {
      switch (type) {
        case 'bar':
        case 'column':
          return renderBarChart();
        case 'line':
          return renderLineChart();
        case 'area':
          return renderAreaChart();
        case 'pie':
        case 'donut':
          return renderPieChart();
        case 'scatter':
          return renderScatterChart();
        default:
          return (
            <div className="chart-error">
              <p>Unsupported chart type: {type}</p>
            </div>
          );
      }
    } catch (error) {
      console.error('Chart rendering error:', error);
      return (
        <div className="chart-error">
          <p>Failed to render chart: {error.message}</p>
        </div>
      );
    }
  };

  return (
    <div className="reports-chart-container">
      <ChartWithCopyButton chartTitle={title}>
        <div className="chart-wrapper">
          {renderChart()}
        </div>
      </ChartWithCopyButton>
      {description && <p className="chart-description">{description}</p>}
    </div>
  );
};

export default ChartRenderer;
